import type {
  FoodActivity,
  DeepPartial,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import { usePolyglot } from "src/contexts"
import { useSliderInputProps } from "src/hooks"

interface Props {
  activity: DeepPartial<FoodActivity>;
  updateActivity: (a: DeepPartial<FoodActivity>) => void;
}

const id = "food-protein-range-input"
const min = 0
const max = 100
const sliderStep = 2
const inputStep = 0.1
const polyglotPrefix = "components.entry_modal.food.protein."

export function ProteinRangeInput(props: Props) {
  const {
    activity,
    updateActivity,
  } = props

  const polyglot = usePolyglot()

  const onChange = useCallback(
    (protein: number | null) => {
      updateActivity({
        ...activity,
        protein,
      })
    },
    [
      activity,
      updateActivity,
    ],
  )

  const value = typeof activity.protein === "number"
    ? activity.protein
    : null

  const {
    inputValue,
    sliderValue,
    onNumberChange,
    onSliderChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    min,
    max,
    inputStep,
    sliderStep,
    value,
    onChange,
    allowEmptyValue: true,
    emptyValue: null,
  })

  return (
    <>
      <SliderValue sizePreset="large">
        <SliderValueInput
          min={min}
          max={max}
          step={inputStep}
          id={id}
          value={inputValue}
          onChange={onNumberChange}
          onBlur={onNumberBlur}
          onKeyDown={onNumberKeyDown}
        />
        <SliderValueLabelContainer unit={polyglot.t(`${polyglotPrefix}label_unit`)}>
          <SliderValueLabel htmlFor={id}>
            {polyglot.t(`${polyglotPrefix}label_detail`)}
          </SliderValueLabel>
        </SliderValueLabelContainer>
      </SliderValue>
      <Slider>
        <SliderInput
          min={min}
          max={max}
          step={sliderStep}
          id={id}
          value={sliderValue ?? min}
          onChange={onSliderChange}
        />
        <SliderTracks
          min={min}
          max={max}
        />
      </Slider>
    </>
  )
}
